import { AuthRequiredError, LOCAL_AUTH } from './constants.ts';
import { entraBroker, localBroker, type AuthBroker, type BrokerToken } from './broker.ts';

/**
 * 이 화면의 로그인 상태. 토큰을 담아 두고, 다시 로그인해야 할 때를 알린다.
 *
 * **여기에 MSAL 이 없다.** 그것은 브로커 뒤에 있고(broker.ts) 이쪽이 하는 일은 셋이다 — 받은
 * 토큰을 만료까지 담아 두는 것, "다시 로그인해야 한다" 를 구독으로 알리는 것, 그리고 그 둘을
 * 화면 전부가 같은 자리에서 보게 하는 것. 그래서 로그인을 어디서 받든 이 파일은 그대로다.
 *
 * 그 신호를 받아 어디로 보낼지는 라우트가 정한다(main.tsx). 그래야 이 파일이 화면의 주소
 * 체계를 몰라도 된다.
 *
 * **로그인한 사람의 이름은 여기서 나오지 않는다.** 브로커가 내는 것은 토큰 하나이고, 어차피
 * 우리가 아는 이름과 역할은 서버가 든다. 화면은 `GET /api/users/me` 를 본다
 * (modules/user/session.ts).
 */

/**
 * 만료 직전의 토큰을 새것처럼 쓰지 않는다.
 *
 * 요청이 날아가는 동안 만료되면 서버가 401 로 답하고 화면은 멀쩡한 세션을 잃은 것처럼 보인다.
 */
const EXPIRY_MARGIN_MS = 60_000;

/** 로그인이 어떻게 끝났는가. 화면이 사람에게 할 말을 이것으로 고른다. */
export type LoginOutcome = 'ok' | 'popup-blocked' | 'not-signed-in';

export const createSession = (broker: AuthBroker) => {
  let cached: BrokerToken | null = null;
  let asking: Promise<BrokerToken | null> | null = null;
  /** 로그아웃이 올린다. 그 전에 나간 물음의 답을 담지 않으려고 센다. */
  let era = 0;
  /** 서버가 우리 토큰을 받지 않았다. 아래 [rejectedByServer] 에 까닭이 있다. */
  let rejected = false;
  const loginNeeded = new Set<() => void>();

  const usable = (token: BrokerToken): boolean =>
    token.expiresOn !== null && token.expiresOn - Date.now() > EXPIRY_MARGIN_MS;

  /**
   * 한 화면이 요청을 여럿 동시에 보낸다. 그 전부가 저마다 브로커를 두드리지 않게 한 번만
   * 묻고 답을 나눠 준다.
   */
  const ask = (): Promise<BrokerToken | null> => {
    asking ??= broker.silent().finally(() => {
      asking = null;
    });
    return asking;
  };

  /**
   * API 를 부를 액세스 토큰.
   *
   * 다시 로그인해야 하는 실패만 AuthRequiredError 로 바꾸고 나머지는 원래 오류 그대로 올린다.
   * 둘을 섞으면 로그인 서비스가 잠깐 흔들린 것만으로 멀쩡한 세션이 로그인 화면으로 쫓겨난다.
   */
  const apiToken = async (): Promise<string> => {
    if (cached !== null && usable(cached)) return cached.accessToken;
    const mine = era;
    const token = await ask();
    // 기다리는 동안 로그아웃했다. 이 답을 담으면 나간 사람이 다시 들어온 것이 된다.
    if (mine !== era) throw new AuthRequiredError('로그아웃했습니다');
    if (token === null) {
      cached = null;
      throw new AuthRequiredError('세션이 끝나 다시 로그인해야 합니다');
    }
    if (token.accessToken === '') {
      cached = null;
      throw new AuthRequiredError('액세스 토큰을 받지 못했습니다');
    }
    cached = token;
    return token.accessToken;
  };

  /**
   * 다시 로그인해야 하는 상태인가.
   *
   * 토큰을 못 얻은 것이 네트워크나 로그인 서비스 탓이면 거짓이다. 그 화면은 열어 두고 "서버에
   * 연결할 수 없습니다" 를 말하게 두는 편이, 멀쩡한 세션을 로그인으로 쫓아내는 것보다 낫다.
   */
  const loginRequired = async (): Promise<boolean> => {
    try {
      await apiToken();
      return false;
    } catch (error) {
      return error instanceof AuthRequiredError;
    }
  };

  /**
   * 지금 요청을 보낼 수 있는가. 로그인 화면이 "이미 들어와 있다" 를 판정하는 자리다.
   *
   * **[loginRequired] 의 반대가 아니다.** 그쪽은 로그인 서비스에 닿지 못한 것을 거짓으로
   * 답한다 — 멀쩡한 세션을 네트워크 사정으로 쫓아내지 않으려는 것이다. 이쪽은 그 경우도
   * 거짓이다. 로그인 화면이 그때 사람을 되돌려 보내면 들어갈 길이 없는 화면으로 보내는 것이고,
   * 사람은 아무 말도 못 듣는다.
   */
  const hasSession = async (): Promise<boolean> => {
    if (rejected) return false;
    try {
      await apiToken();
      return true;
    } catch {
      return false;
    }
  };

  /**
   * 서버가 우리 토큰을 401 로 거절했다.
   *
   * 브로커는 멀쩡히 내주는데 서버가 안 받는 자리가 있다. 우리가 청하는 범위 이름과 서버가 보는
   * 이름이 어긋났을 때가 그렇다(auth/constants.ts 의 ENTRA_API_SCOPE, platform/auth.ts). 그때 담아 둔 것만 버리고 로그인으로 보내면, 그
   * 화면이 브로커에 물어 "들어와 있다" 고 판정해 되돌려 보내고, 그 화면이 또 401 을 받는다.
   * 두 화면이 서로를 부르며 끝나지 않는다.
   *
   * 그래서 이 표시가 [hasSession] 을 눌러 둔다. 사람이 다시 로그인을 누르면 풀린다 — 토큰이
   * 그냥 만료됐던 것이면 그 한 번으로 풀리고, 설정이 어긋난 것이면 같은 자리로 돌아온다.
   */
  const rejectedByServer = (): void => {
    cached = null;
    rejected = true;
    sendToLogin();
  };

  /** 서버가 받지 않는 토큰을 들고 있는가. 로그인 화면이 할 말을 고르는 자리다. */
  const serverRejected = (): boolean => rejected;

  /** 한 화면이 요청을 여럿 동시에 보낸다. 한 번만 통과시키는 자리는 이 신호를 받는 쪽이다. */
  const sendToLogin = (): void => {
    for (const handle of loginNeeded) handle();
  };

  /**
   * 로그인 화면의 단추가 부르는 자리. **사람이 누른 자리에서만** 부른다.
   *
   * 팝업은 누름에서만 열린다. 가드나 요청 앞자리에서 부르면 브라우저가 막고, 막힌 것과 로그인
   * 실패를 화면이 구별할 수 없게 된다.
   *
   * 토큰은 이 자리에서 나오지 않는다. 팝업은 계정을 앉힐 뿐이고 토큰은 닫힌 뒤 브로커에 다시
   * 묻는다. 받는 길이 하나라 담아 두는 자리도 하나다.
   */
  const login = async (): Promise<LoginOutcome> => {
    if (!(await broker.signIn())) return 'popup-blocked';
    cached = null;
    // 사람이 다시 누른 것이다. 서버의 판정도 다시 받는다.
    rejected = false;
    try {
      await apiToken();
      return 'ok';
    } catch (error) {
      if (error instanceof AuthRequiredError) return 'not-signed-in';
      throw error;
    }
  };

  /**
   * 나간다.
   *
   * 담아 둔 토큰을 먼저 버린다. 액세스 토큰은 한 시간까지 살아 있어서, 버리지 않으면 나간
   * 사람의 요청이 그 동안 그대로 통한다. 나가는 동안 들어온 요청이 그것을 다시 담을 수 있어
   * 끝난 뒤에도 한 번 더 버린다.
   */
  const logout = async (): Promise<void> => {
    era += 1;
    cached = null;
    asking = null;
    try {
      await broker.signOut();
    } finally {
      cached = null;
      sendToLogin();
    }
  };

  return {
    apiToken,
    loginRequired,
    hasSession,
    rejectedByServer,
    serverRejected,
    login,
    logout,
    sendToLogin,
    /** 다시 로그인해야 한다는 신호. 라우트가 이것을 받아 /login 으로 보낸다. */
    onLoginRequired: (handle: () => void): (() => void) => {
      loginNeeded.add(handle);
      return () => loginNeeded.delete(handle);
    },
  };
};

const session = createSession(LOCAL_AUTH ? localBroker : entraBroker);

export const apiToken = session.apiToken;
export const loginRequired = session.loginRequired;
export const hasSession = session.hasSession;
export const rejectedByServer = session.rejectedByServer;
export const serverRejected = session.serverRejected;
export const login = session.login;
export const logout = session.logout;
export const sendToLogin = session.sendToLogin;
export const onLoginRequired = session.onLoginRequired;
